import { LayoutId, WeekdayTitleStyle } from 'modules/map/components/daily-dish/constants';
import { getLayoutConfig } from 'modules/map/components/daily-dish/config';
import { defaultSettings, TemplateSettings } from 'modules/map/components/daily-dish/types';
import { EMOJI_COUNT } from 'modules/map/components/daily-dish/look/emojiCatalog';
import { rngFor } from 'modules/map/components/daily-dish/look/seed';
import type {
  HolidayWindow,
  LookPalette,
  WeeklyLookConfig,
} from 'modules/map/components/daily-dish/look/types';
import {
  WEEKLY_LOOK_CONFIG,
  WINDOW_LABELS,
} from 'modules/map/components/daily-dish/look/weeklyLookConfig';
import { schoolYearDateRange } from 'common/utils/schoolYearMonths';

const isoDay = (year: number, md: string): string => {
  const [month, day] = md.split('-');
  return `${year}-${month}-${day}`;
};

const rangesOverlap = (fromA: string, toA: string, fromB: string, toB: string): boolean =>
  fromA <= toB && fromB <= toA;

export const weekOverlapsWindow = (
  dateFrom: string,
  dateTo: string,
  window: HolidayWindow
): boolean => {
  if (!window.everyYear) {
    return rangesOverlap(dateFrom, dateTo, window.start, window.end);
  }

  const years = new Set([Number(dateFrom.slice(0, 4)), Number(dateTo.slice(0, 4))]);
  for (const year of years) {
    const start = isoDay(year, window.start);
    let end = isoDay(year, window.end);
    if (end < start) {
      end = isoDay(year + 1, window.end);
    }
    if (rangesOverlap(dateFrom, dateTo, start, end)) {
      return true;
    }
  }
  return false;
};

export const resolveWindowId = (
  dateFrom: string,
  dateTo: string,
  lockedTheme: string | null,
  config: WeeklyLookConfig = WEEKLY_LOOK_CONFIG
): string => {
  if (lockedTheme === 'general') {
    return 'general';
  }
  if (lockedTheme) {
    const locked = config.windows.find((window) => window.id === lockedTheme);
    if (locked) {
      return locked.id;
    }
    return 'general';
  }

  const matched = config.windows.find(
    (window) => window.images.length > 0 && weekOverlapsWindow(dateFrom, dateTo, window)
  );
  return matched?.id ?? 'general';
};

const poolFor = (
  windowId: string,
  config: WeeklyLookConfig
): { images: string[]; palettes: LookPalette[] } => {
  if (windowId === 'general') {
    return config.general;
  }
  const window = config.windows.find((item) => item.id === windowId);
  if (!window) {
    return config.general;
  }
  return {
    images: window.images.length > 0 ? window.images : config.general.images,
    palettes: window.palettes.length > 0 ? window.palettes : config.general.palettes,
  };
};

const pickIndex = (rng: () => number, length: number): number => {
  if (length <= 0) {
    return 0;
  }
  return Math.floor(rng() * length);
};

const shiftIndex = (seed: number, offset: number, length: number): number => {
  if (length <= 0) {
    return 0;
  }
  return (((seed + offset) % length) + length) % length;
};

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

const lcm = (a: number, b: number): number => (a / gcd(a, b)) * b;

const PRESET_LAYOUTS = [LayoutId.SIX_COLUMNS, LayoutId.TWO_ROWS] as const;

export const lookCycleLength = (pool: { images: string[]; palettes: LookPalette[] }): number => {
  const palettes = Math.max(pool.palettes.length, 1);
  return lcm(lcm(Math.max(pool.images.length, 1), palettes), PRESET_LAYOUTS.length);
};

const pickPalette = (
  palettes: LookPalette[],
  dateFrom: string,
  offset: number
): LookPalette | null => {
  if (palettes.length === 0) {
    return null;
  }
  return palettes[
    shiftIndex(pickIndex(rngFor(dateFrom, 'palette'), palettes.length), offset, palettes.length)
  ];
};

const shuffleIds = (rng: () => number, count: number): number[] => {
  const ids = Array.from({ length: count }, (_, index) => index + 1);
  for (let i = ids.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const current = ids[i];
    ids[i] = ids[j];
    ids[j] = current;
  }
  return ids;
};

const applyPalette = (base: TemplateSettings, palette: LookPalette): TemplateSettings => ({
  ...base,
  font: {
    weekTitle: {
      ...base.font.weekTitle,
      fontFamilyId: palette.fontFamilyId,
      fontColor: palette.weekTitleColor,
    },
    subWeekTitle: {
      ...base.font.subWeekTitle,
      fontFamilyId: palette.fontFamilyId,
      fontColor: palette.subWeekTitleColor,
    },
    weekdayTitle: {
      ...base.font.weekdayTitle,
      fontFamilyId: palette.fontFamilyId,
      fontColor: palette.weekdayTitleColor,
    },
    content: {
      ...base.font.content,
      fontFamilyId: palette.fontFamilyId,
      fontColor: palette.contentColor,
    },
  },
  menu: {
    ...base.menu,
    borderWidth: palette.borderWidth,
    borderRadius: palette.borderRadius,
    emojiEnabled: true,
    weekday: {
      2: { ...base.menu.weekday[2], borderColor: palette.weekday[2].borderColor },
      3: { ...base.menu.weekday[3], borderColor: palette.weekday[3].borderColor },
      4: { ...base.menu.weekday[4], borderColor: palette.weekday[4].borderColor },
      5: { ...base.menu.weekday[5], borderColor: palette.weekday[5].borderColor },
      6: { ...base.menu.weekday[6], borderColor: palette.weekday[6].borderColor },
      7: { ...base.menu.weekday[7], borderColor: palette.weekday[7].borderColor },
    },
  },
});

export const pickWeeklyLook = (args: {
  dateFrom: string;
  dateTo: string;
  lockedTheme?: string | null;
  lookOffset?: number;
  config?: WeeklyLookConfig;
}): TemplateSettings => {
  const config = args.config ?? WEEKLY_LOOK_CONFIG;
  const offset = args.lookOffset ?? 0;
  const windowId = resolveWindowId(args.dateFrom, args.dateTo, args.lockedTheme ?? null, config);
  const pool = poolFor(windowId, config);

  const imageFile =
    pool.images.length > 0
      ? pool.images[
          shiftIndex(
            pickIndex(rngFor(args.dateFrom, 'image'), pool.images.length),
            offset,
            pool.images.length
          )
        ]
      : defaultSettings.general.backgroundFile;

  const palette = pickPalette(pool.palettes, args.dateFrom, offset);

  const layoutId =
    PRESET_LAYOUTS[
      shiftIndex(
        pickIndex(rngFor(args.dateFrom, 'layout'), PRESET_LAYOUTS.length),
        offset,
        PRESET_LAYOUTS.length
      )
    ];
  const titlePosition =
    shiftIndex(pickIndex(rngFor(args.dateFrom, 'titlePos'), 2), offset, 2) === 0 ? 'top' : 'bottom';
  const canChangeTitleStyle = getLayoutConfig(layoutId)?.capabilities.canChangeTitleStyle ?? true;
  const titleStyle = canChangeTitleStyle
    ? shiftIndex(pickIndex(rngFor(args.dateFrom, 'titleStyle'), 2), offset, 2) === 0
      ? WeekdayTitleStyle.PINNED
      : WeekdayTitleStyle.NORMAL
    : WeekdayTitleStyle.NORMAL;

  const emojiStream = offset === 0 ? 'emoji' : `emoji:${offset}`;
  const emojiIds = shuffleIds(rngFor(args.dateFrom, emojiStream), EMOJI_COUNT);

  let settings: TemplateSettings = {
    ...defaultSettings,
    general: {
      ...defaultSettings.general,
      layoutId,
      layoutAlign: defaultSettings.general.layoutAlign,
      backgroundFile: imageFile,
      localBackground: false,
    },
    menu: {
      ...defaultSettings.menu,
      weekdayTitleStyle: titleStyle,
      weekdayTitlePosition: titlePosition,
      emojiEnabled: true,
    },
    favoritesList: [],
  };

  if (palette) {
    settings = applyPalette(settings, palette);
  }

  return {
    ...settings,
    menu: {
      ...settings.menu,
      weekday: {
        2: { ...settings.menu.weekday[2], emojiId: emojiIds[0] },
        3: { ...settings.menu.weekday[3], emojiId: emojiIds[1] },
        4: { ...settings.menu.weekday[4], emojiId: emojiIds[2] },
        5: { ...settings.menu.weekday[5], emojiId: emojiIds[3] },
        6: { ...settings.menu.weekday[6], emojiId: emojiIds[4] },
        7: { ...settings.menu.weekday[7], emojiId: emojiIds[5] },
      },
    },
  };
};

export const cycleWeeklyLook = (args: {
  dateFrom: string;
  dateTo: string;
  lockedTheme?: string | null;
  lookOffset: number | null;
  config?: WeeklyLookConfig;
}): { settings: TemplateSettings; lookOffset: number } => {
  const config = args.config ?? WEEKLY_LOOK_CONFIG;
  const windowId = resolveWindowId(args.dateFrom, args.dateTo, args.lockedTheme ?? null, config);
  const pool = poolFor(windowId, config);
  const length = lookCycleLength(pool);
  const current = args.lookOffset ?? 0;
  const lookOffset = (current + 1) % length;
  return {
    settings: pickWeeklyLook({ ...args, lookOffset, config }),
    lookOffset,
  };
};

/** Lưới chọn nền: đúng pool đang khóa. Window chưa map ảnh → `[]` (không đổ Ảnh chung). */
export const imagesForWindow = (
  windowId: string,
  config: WeeklyLookConfig = WEEKLY_LOOK_CONFIG
): string[] => {
  if (windowId === 'general' || windowId === '') {
    return config.general.images;
  }
  const window = config.windows.find((item) => item.id === windowId);
  if (!window) {
    return config.general.images;
  }
  return window.images;
};

export const themeLockOptions = (
  schoolYear?: number | null,
  config: WeeklyLookConfig = WEEKLY_LOOK_CONFIG
): { value: string; label: string }[] => {
  const options = [
    { value: 'week', label: 'Theo tuần' },
    { value: 'general', label: WINDOW_LABELS.general },
  ];
  const range = schoolYear && Number.isInteger(schoolYear) ? schoolYearDateRange(schoolYear) : null;
  config.windows.forEach((window) => {
    if (!window.everyYear) {
      if (!range || !weekOverlapsWindow(range.from, range.to, window)) {
        return;
      }
    }
    options.push({
      value: window.id,
      label: WINDOW_LABELS[window.id] ?? window.id,
    });
  });
  return options;
};
